/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import type {
  TCarouselItem,
  TMyHubActionType,
  TMyHubBulkActionType
} from "../../model/types";
import { CarouselTemplate } from "../../model/enums";
import type { TTilePaginationState } from "../utility/carouselPagination";
import type { TMyHubFilterMeta } from "./myHubFilters";

/**
 * ViewModel state and presentation-ready shapes for the My Hub feature.
 *
 * The service's own contracts — the response envelopes, the item variants and
 * the affordance enums — are Domain and live in `model/types/TMyHubDto.ts`. This
 * file holds only what exists because of the screens: focus regions, sort and
 * filter identity, and the already-composed rows the mappers hand to the views.
 */

/**
 * Identifiers for the My Hub content filter chips. Each is passed verbatim as
 * the `myHubFilter` query parameter of `GET /v1/myHub/carousels`.
 *
 * The service treats `myHubFilter` as an opaque string ("the filter name") and
 * publishes the valid values through `GET /v1/myHub/myHubFilters`, which returns
 * `{ value, label }` pairs — so the tab strip's chips (and their labels) are
 * server-driven and fetched by `fetchMyHubFilters` in `myHubSlice.ts`. This enum
 * is still how the rest of the client identifies a filter internally: a fetched
 * `value` that does not match one of these members is dropped rather than kept
 * as a raw string, so every reducer and thunk downstream can keep matching on
 * `MyHubFilterId` instead of comparing strings.
 */
export enum MyHubFilterId {
  RECORDED = "recorded",
  SCHEDULED = "scheduled",
  SAVED = "saved",
  DELETED = "deleted"
}

/** screen_id for every My Hub tab, per the `my_hub` screen registry entry. */
export const MY_HUB_SCREEN_ID: string = "my_hub";

/** screen_name for every My Hub tab, per the `my_hub` screen registry entry. */
export const MY_HUB_SCREEN_NAME: string = "MyHub";

/**
 * `list_name` values for the My Hub chrome — the control groups that are not
 * a content list. Named constants rather than inline literals so the analytics
 * dimension has one definition per surface and cannot drift between the
 * pointer and D-pad paths that both report it.
 */
export const MY_HUB_FILTER_STRIP_LIST_NAME: string = "MyHubFilters";
export const MY_HUB_SORT_CONTROL_LIST_NAME: string = "MyHubSort";
export const MY_HUB_FOLDER_ACTIONS_LIST_NAME: string =
  "ManageSeriesFolderActions";
export const MY_HUB_DELETED_BULK_LIST_NAME: string = "MyHubDeletedBulkAction";

/**
 * `list_name` for a row action (save/unsave/delete/undelete/…) on the Saved
 * or Deleted flat-list tab — distinct from {@link MY_HUB_SCREEN_NAME}, which
 * names the screen, not the list a row sits in. Only these two tabs render
 * `MyHubSavedList`'s row actions; Recorded/Scheduled tiles go through the
 * recording-highlight popup instead.
 */
export const MY_HUB_SAVED_LIST_NAMES: Readonly<
  Partial<Record<MyHubFilterId, string>>
> = {
  [MyHubFilterId.SAVED]: "Saved",
  [MyHubFilterId.DELETED]: "Deleted"
};

/**
 * {@link MY_HUB_SAVED_LIST_NAMES} for `filter`, or `""` for the two carousel
 * tabs that have no flat list. One resolver so the pointer path, the D-pad
 * path and the dwell timer cannot report the row under three different names.
 */
export const resolveMyHubSavedListName = (filter: MyHubFilterId): string => {
  const listName: string | undefined = MY_HUB_SAVED_LIST_NAMES[filter];
  return listName === undefined ? "" : listName;
};

/**
 * contentHighlighted dwell threshold, shared by every My Hub surface that
 * tracks tile/row focus (the carousel tabs, the Saved/Deleted flat list, and
 * the manage-series/scheduled-series/scheduled-single row lists).
 */
export const MY_HUB_CONTENT_HIGHLIGHT_DWELL_MS: number = 500;

/**
 * Identifiers for the My Hub sort order control chips.
 *
 * These are the client's own ids, chosen to match the chip labels ("Recent",
 * "A-Z"); the API's `orderBy` parameter takes `byDate` / `byName` instead. Use
 * {@link MY_HUB_ORDER_BY} to translate — the two vocabularies are deliberately
 * kept apart so a label change does not become a wire change.
 */
export enum MyHubSortId {
  RECENT = "recent",
  ALPHABETICAL = "alphabetical"
}

/** Values the `orderBy` query parameter accepts. */
export type TMyHubOrderBy = "byDate" | "byName";

/** Maps a sort chip onto the `orderBy` value the API expects. */
export const MY_HUB_ORDER_BY: Readonly<Record<MyHubSortId, TMyHubOrderBy>> = {
  [MyHubSortId.RECENT]: "byDate",
  [MyHubSortId.ALPHABETICAL]: "byName"
};

/**
 * D-pad / pointer focus region for the My Hub screen.
 * The active region determines which row of controls or carousel row
 * receives directional input routed from the ScreenController.
 */
export enum MyHubFocusRegion {
  FILTERS = "filters",
  SORT = "sort",
  CAROUSEL_ITEMS = "carouselItems",
  /**
   * The flat vertical card list shared by the Saved and Deleted tabs
   * (Figma: My Hub Detail — nodes 115:780 and 71:2130). Unlike CAROUSEL_ITEMS,
   * UP/DOWN moves between rows and LEFT/RIGHT moves between the focused row's
   * action buttons.
   */
  SAVED_LIST = "savedList",
  /**
   * The flat list's tab-level bulk button, which sits on the summary line above
   * the rows ("permanently Delete All" — Figma node 71:2376). Its own region
   * because it is a single control between the tab strip and the first row, so
   * DOWN out of the filters lands here before entering the list.
   */
  BULK_ACTION = "bulkAction",
  /**
   * The row of collection-level buttons in the manage-series header
   * ("Recordings Options", "Cancel Series Recordings", "Delete All Recordings" —
   * Figma node 71:4516). Its own region because those buttons sit where the
   * landing screen's tab strip does, and LEFT/RIGHT walks between them.
   */
  SERIES_ACTIONS = "seriesActions"
}

/**
 * Mapped carousel for the My Hub view layer.
 * Each carousel contains carousel metadata and a list of mapped carousel items.
 */
export interface TMyHubCarouselData {
  carouselId: string;
  title: string | undefined;
  layout: CarouselTemplate;
  resolvedCount: number | undefined;
  items: TCarouselItem[];
}

/**
 * Carousel ROW pagination state for My Hub — holds the carousels fetched from
 * GET /v1/myHub/carousels.
 *
 * `totalCount` is the total number of carousel ROWS across the whole grid,
 * from the response's X-Total-Count header (see
 * `MyShowsInteractor.fetchMyHubCarousels`) — not to be confused with each
 * row's own `TMyHubCarouselData.resolvedCount` (tile count within that row).
 * `null` until the first response.
 */
export interface TMyHubCarouselPaginationState {
  items: TMyHubCarouselData[];
  totalCount: number | null;
  isLoading: boolean;
  isLoadingMore: boolean;
  error: string;
  requestId: string | null;
}

/**
 * Filter/tab strip state for My Hub — holds the chips fetched from
 * GET /v1/myHub/myHubFilters.
 */
export interface TMyHubFiltersState {
  items: TMyHubFilterMeta[];
  isLoading: boolean;
  error: string;
  requestId: string | null;
}

/**
 * Recording-quota snapshot rendered by the My Hub header indicator
 * ("Free  50h 20m / 100h" — Figma: My Hub Detail, node 481:461).
 *
 * Both values are whole minutes so the view formats them without having to
 * re-derive units.
 *
 * Sourced from GET /v1/diskSpace (`StorageInfo`), which is separate from the
 * Saved tab's own allowance — that arrives as `MyHubCarouselHeader.usedMinutes`
 * / `limitMinutes` on that tab's carousel.
 */
export interface TMyHubRecordingQuota {
  /** Recording time still available on the subscriber's plan, in minutes. */
  freeMinutes: number;
  /** Total recording time included in the subscriber's plan, in minutes. */
  totalMinutes: number;
}

/**
 * One presentation-ready row of the flat card list shared by the Saved and
 * Deleted tabs (Figma: My Hub Detail — nodes 115:780 and 71:2130).
 *
 * Every string is already composed and formatted for display, so the view layer
 * renders it verbatim: the mapper in `myHubSavedMappers.ts` owns date
 * formatting, duration wording and the headline composition. The action ids are
 * the raw API affordances — the view maps them onto card buttons.
 */
export interface TMyHubSavedItem {
  /** MyHub item id — React key and focus identity. */
  id: string;
  /** Recording id, for the delete / unsave / play calls. Absent is a data bug. */
  recordingId: string | undefined;
  /** Content id, for routing to the content detail screen. */
  contentId: string | undefined;
  /** Composed single line: "Yellowstone  -  S5 E10 ‧ Life is a Promise". */
  headline: string;
  /**
   * The row's dated line — "Saved on  5/18/2026" on the Saved tab,
   * "Deleted on  5/18/2026" on the Deleted tab. Empty when the API omits the
   * date the active tab reads.
   */
  dateLabel: string;
  /** "Expires on 6/17/2026" — empty when the recording does not expire. */
  expiresOnLabel: string;
  /**
   * Thumbnail caption shown on unfocused rows: "Saved ‧ 1 hour". Empty on the
   * Deleted tab, whose thumbnails carry no caption (Figma node 71:2130).
   */
  durationLabel: string;
  /** Landscape thumbnail URL; empty falls back to the card's title stand-in. */
  imageUrl: string;
  /** Watch progress 0–100, or `undefined` when never watched. */
  progressPercent: number | undefined;
  /** Actions the API says are available on this item, in API order. */
  availableActions: TMyHubActionType[];
  /**
   * The tile this row was mapped from, carried so an action can act on it —
   * `performMyHubAction` takes a tile, and playback reads the offer type and
   * station id off it. Mirrors `TMyHubRecordingHighlightItem.tile`.
   */
  tile: TCarouselItem;
}

/**
 * Tab-level summary rendered above the flat list — Figma:
 * "Total Saved: 3 recordings ‧ 10 / 10 hours" (node 115:988) and
 * "Total Deleted: 3 recordings / used 10 hours" (node 71:2338).
 *
 * Mapped from {@link TMyHubCarouselHeader}; minutes rather than hours so the
 * view does the unit formatting in one place.
 */
export interface TMyHubSavedListSummary {
  /** Number of recordings on the tab. */
  totalCount: number;
  /** Recording time used by the tab's items, in minutes. */
  usedMinutes: number;
  /** Allowance for the tab, in minutes. `0` when the tab has none. */
  limitMinutes: number;
  /**
   * Tab-level bulk affordance the API offers beside the summary line
   * ("permanently Delete All" on the Deleted tab), or `null` when it offers
   * none — the Saved tab's header carries no bulk action.
   */
  bulkAction: TMyHubBulkActionType | null;
}

/**
 * Redux state for the flat list shared by the Saved and Deleted tabs.
 *
 * Held separately from `carouselsPagination` because neither tab is a carousel
 * surface: each is one vertical list plus a header summary, and both use the
 * same two-axis focus model. One slot rather than one per tab — only ever one
 * of the two is mounted, and each tab refetches as it becomes active.
 */
export interface TMyHubSavedListState {
  items: TMyHubSavedItem[];
  /** `null` until the response arrives — the view hides the summary line. */
  summary: TMyHubSavedListSummary | null;
  /**
   * Id of the single carousel backing this flat list, for paging further pages
   * via GET /v1/myHub/carousels/{carouselId} — the same endpoint carousel row
   * tiles page through. `null` until the first response.
   */
  carouselId: string | null;
  /**
   * True length of the list per the server, driving offset/count paging the
   * same way a carousel row's `resolvedCount` does. `null` until the first
   * response.
   */
  resolvedCount: number | null;
  isLoading: boolean;
  /** True while a further page is being appended past the initial fetch. */
  isLoadingMore: boolean;
  error: string;
  requestId: string | null;
}

/**
 * A composed series heading, whole and split at the point the view is allowed
 * to truncate — see `composeSeriesHeading`.
 */
export interface TMyHubSeriesHeading {
  /** The full heading, e.g. "Yellowstone Scheduled Recordings". */
  text: string;
  /** The series name alone, e.g. "Yellowstone". */
  name: string;
  /** The appended wording alone, e.g. "Scheduled Recordings". */
  suffix: string;
}

/**
 * Header of the manage-series screen — Figma node 71:4516 renders
 * "Yellowstone Recordings" over "12 Episodes ‧ 12 hours", with the folder's
 * buttons on the row beneath.
 *
 * Mapped from {@link TMyHubFolderDetail}. The title is server-owned rather than
 * composed here: the service distinguishes "Yellowstone Recordings" from
 * "Yellowstone Scheduled Recordings", and only it knows which variant the
 * requested filter produced.
 */
export interface TMyHubSeriesSummary {
  /** Screen title, verbatim from the service. */
  title: string;
  /** Screen heading, typically the series name plus the type of recordings ("Recorded" or "Scheduled").   */
  heading: string;
  /**
   * `heading`'s first half — the series name on its own.
   *
   * The screen renders the two halves as separate spans so the heading always
   * fits on one line: this one ellipsises, {@link headingSuffix} never does.
   */
  headingName: string;
  /**
   * `heading`'s second half — "Recordings" or "Scheduled Recordings", without
   * the separating space.
   *
   * Empty only if a locale ever drops the wording entirely, in which case the
   * view renders the name alone.
   */
  headingSuffix: string;
  /** Number of items in the folder. */
  episodeCount: number;
  /** Combined runtime of the folder's recordings, in minutes. */
  totalMinutes: number;
  /**
   * Collection-level buttons the service offers in the header, in its own
   * order. Empty when it offers none — the row is then not rendered.
   */
  folderActions: TMyHubBulkActionType[];
}

/**
 * Redux state for the manage-series screen.
 *
 * Rows are {@link TMyHubSavedItem}, the same presentation-ready shape the
 * Saved / Deleted flat list renders: the two designs are the same row, so the
 * screen reuses `MyHubSavedList` rather than growing a second row component.
 * Only the composed copy differs, and the mapper owns that.
 */
export interface TMyHubSeriesState {
  items: TMyHubSavedItem[];
  /** `null` until the response arrives — the view hides the header text. */
  summary: TMyHubSeriesSummary | null;
  isLoading: boolean;
  error: string;
  /** Collection the loaded folder belongs to, or `""` before the first fetch. */
  unifiedCollectionId: string;
  requestId: string | null;
}

/**
 * Redux state for the My Hub screen.
 */
export interface MyHubState {
  /** Currently active content filter. Defaults to ALL (no filter applied). */
  selectedFilter: MyHubFilterId;
  /** Currently active sort order. */
  selectedSort: MyHubSortId;
  /** The filter/tab strip fetched from GET /v1/myHub/myHubFilters. */
  filters: TMyHubFiltersState;
  /** Carousel data fetched and mapped from GET /v1/myHub/carousels. */
  carouselsPagination: TMyHubCarouselPaginationState;
  /**
   * Offset-paginated tiles per carousel row, keyed by carouselId.
   *
   * Seeded from the initial GET /v1/myHub/carousels response and extended by
   * paging GET /v1/myHub/carousels/{carouselId} — the same
   * resolvedCount-driven offset pattern Home Screen uses for its rails (see
   * `HomescreenState.tilePagination`), reusing the same shared
   * `carouselPagination.ts` helpers rather than a My Hub-specific mechanism.
   */
  tilePagination: Record<string, TTilePaginationState>;
  /**
   * The flat card list of whichever of the Saved / Deleted tabs is active,
   * fetched separately from the carousels.
   */
  savedList: TMyHubSavedListState;
  /**
   * The folder currently open on the manage-series screen (Figma node 71:4516).
   *
   * A separate slot from `savedList` even though both hold `TMyHubSavedItem`
   * rows: the two are different responses about different things, and the
   * screens are reached independently, so one must not evict the other.
   */
  series: TMyHubSeriesState;
  /**
   * Index of the focused button in the manage-series header's folder-action row.
   * Meaningful only while `focusRegion` is
   * {@link MyHubFocusRegion.SERIES_ACTIONS}.
   */
  focusedSeriesActionIndex: number;
  /**
   * Index of the focused row in whichever flat card list is on screen — the
   * Saved / Deleted tab's, or the manage-series screen's. Meaningful only while
   * `focusRegion` is {@link MyHubFocusRegion.SAVED_LIST}.
   *
   * Shared by both surfaces rather than held per screen: they render the same
   * list through the same component, only one is ever mounted, and each clamps
   * the index against its own rows as it loads.
   */
  focusedSavedIndex: number;
  /**
   * Index into the focused row's action buttons. Reset to 0 whenever the
   * focused row changes, so LEFT/RIGHT always starts from the row's default
   * action rather than carrying a stale column across rows with different
   * action sets.
   */
  focusedSavedActionIndex: number;
  /**
   * Recording quota shown in the screen header, shared by every filter tab.
   * `null` until the API response arrives — the indicator stays hidden while
   * the value is unknown rather than rendering a misleading placeholder.
   */
  recordingQuota: TMyHubRecordingQuota | null;
  /** Which UI region currently holds D-pad / pointer focus. */
  focusRegion: MyHubFocusRegion;
  /** True once BACK is pressed, so the unmount resets rather than persists. */
  isExiting: boolean;
  /** Index of the highlighted filter chip in the filter row. */
  focusedFilterIndex: number;
  /** Index of the highlighted sort chip in the sort row. */
  focusedSortIndex: number;
  /** Index of the focused carousel row in the content area. */
  focusedCarouselIndex: number;
  /** Index of the focused tile within the active carousel row. */
  focusedItemIndex: number;
  /**
   * Scroll position (item index) for each carousel, maintained independently.
   * Allows carousels to preserve their scroll state when navigating away.
   * Length matches carouselItemCounts array.
   */
  carouselScrollPositions: number[];
  /**
   * Item count per visible carousel row, in order.
   * Synced from the view via setCarouselItemCounts whenever the visible
   * carousel list changes. Allows navigation reducers to compute bounds
   * without receiving payload from the view layer.
   *
   * TODO(UAW): Remove once carousel data is held directly in this slice
   * and the reducers can derive counts from state.carousels.
   */
  carouselItemCounts: number[];
  /**
   * Set alongside a focus reset to a surface's first item — a
   * `refreshMyHubAfterAction({ moveFocusToFirstItem: true })` call, or the
   * carousel-grid refresh `exitMyHubSeriesScreen` runs on the way out of a
   * folder — so the screen's own scroll-to-focus effect snaps there
   * instantly instead of animating. Seeing the whole carousel/list glide
   * back to the top right after an action or a screen exit reads as a
   * glitch, not a refresh. The screen clears it back to false once it has
   * consumed it for one scroll.
   */
  scrollInstantly: boolean;
  /**
   * Set while a manage-series/scheduled-series folder screen is open and a
   * mutation that can change what the carousel grid behind it shows has gone
   * through — a single-episode delete, Cancel Series Recordings, or Delete
   * All Recordings. Read by {@link exitMyHubSeriesScreen} on a real exit
   * (BACK out of the folder, not a drill-in to Play/Details): when true, the
   * whole carousel grid is refetched and the cursor lands on its first tile,
   * instead of the landing screen showing whatever it had before the folder
   * was opened. Cleared once that refetch has been dispatched.
   */
  carouselRefreshPending: boolean;
  /**
   * The landing screen's sort, remembered when the screen is left for a
   * drill-in (`persistMyHubState`) and restored when a folder screen is left
   * for real (`leaveMyHubSeriesScreen`). A folder always opens sorted by
   * Recent, so without this the landing screen would come back with the
   * folder's sort instead of its own.
   */
  landingSort: MyHubSortId;
}
